import React, { useState, useEffect, useRef, useCallback, KeyboardEvent as ReactKeyboardEvent } from "react";
import { useTranslation } from 'react-i18next';
import debounce from "lodash.debounce";
import { SearchIcon } from "./Icon";
import { lazyWithRetry } from "../utils/lazyWithRetry";
import EmojiSelector from './EmojiSelector';

const EmojiExplanationModal = lazyWithRetry(() =>
  import("./EmojiExplanationModal").then((m) => ({ default: m.EmojiExplanationModal }))
);
import API_BASE_URL from "../utils/ApiConfig";
import RestoApiService from "../utils/RestoApiService";
import { SuggestionListItem, StatusItem, Suggestion } from "./SuggestionListItem";
import AnalyticsManager from "../utils/AnalyticsManager";
import { useModalState } from "../hooks/useModalState";
import { useRecentlyViewed, clearRecentlyViewed } from "../hooks/useRecentlyViewed";
import "./SearchComponent.css";


interface SelectedRestaurantInfo {
  id: string | number;
  location: google.maps.LatLngLiteral;
}

interface SearchComponentProps {
  onRestaurantSelect: (data: SelectedRestaurantInfo | null) => void; 
  selectedRestaurantId: string | number | null; 
  mapBounds?: google.maps.LatLngBounds | null; 
  onEmojiRangeChange?: (emojiRange: number[]) => void; 
}

export function SearchComponent({ onRestaurantSelect, selectedRestaurantId, mapBounds, onEmojiRangeChange }: SearchComponentProps) {
  const { t } = useTranslation();
  const { modals, openModal, closeModal } = useModalState();
  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displaySuggestions, setDisplaySuggestions] = useState<Suggestion[]>([]);
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState<number>(-1);
  
  // New state to track if a suggestion has been selected
  const [isSuggestionSelected, setIsSuggestionSelected] = useState(false);

  // Recently viewed restaurants, shown when the search field is focused but
  // (nearly) empty
  const recentlyViewed = useRecentlyViewed();
  const [isFocused, setIsFocused] = useState(false);
  const showRecentlyViewed = isFocused && inputValue.length < 2 && recentlyViewed.length > 0;
  
  const activeSuggestionRef = useRef<HTMLLIElement | null>(null); 
  
  const [selectedEmojis, setSelectedEmojis] = useState<boolean[]>(() => {
    
    return [true, true, true, true, true, false]; 
  });
  const emojis = ['💀', '🤮', '🙂', '😃', '😍', '❓']; 
  
  const mapIndexToScore = (index: number): number => {
    if (index === 5) return 0; 
    return index + 1; 
  };

  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const emojiScores = selectedEmojis
      .map((isSelected, index) => isSelected ? mapIndexToScore(index) : -1)
      .filter(score => score !== -1);

    if (onEmojiRangeChange) {
      if (emojiScores.length === 0) {
        onEmojiRangeChange([]);
        
      } else {
        onEmojiRangeChange(emojiScores);
        
      }
    }
  }, [onEmojiRangeChange, selectedEmojis]);
  
  const prevSelectedIdRef = useRef<string | number | null>(null);
  
  useEffect(() => {
    if (selectedRestaurantId === null && prevSelectedIdRef.current !== null) {
      setInputValue("");
      setSuggestions([]);
      setDisplaySuggestions([]);
    }
    
    prevSelectedIdRef.current = selectedRestaurantId;
  }, [selectedRestaurantId]);

  const fetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (!query || query.length < 2) {
        // Don't clear suggestions immediately - just stop loading
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        
        
        
        
        
        const baseApiParams: Record<string, string> = { q: query };
        
        if (mapBounds) {
          const boundsJson = mapBounds.toJSON();
          
          baseApiParams.left = String(boundsJson.north);
          baseApiParams.top = String(boundsJson.west);
          baseApiParams.right = String(boundsJson.south);
          baseApiParams.bottom = String(boundsJson.east);
        } else {
          // No map bounds available - will search globally
        }
        
        const currentSelectedScores = selectedEmojis
          .map((isSelected, index) => isSelected ? mapIndexToScore(index) : -1)
          .filter(score => score !== -1);

        
        
        const finalApiParamsToSend = { ...baseApiParams };
        
        if (currentSelectedScores && currentSelectedScores.length > 0) {
          finalApiParamsToSend.forks = currentSelectedScores.join(',');
          
        } else {
          delete finalApiParamsToSend.forks;
          
          
          if ('forks' in finalApiParamsToSend) {
            console.error("[SearchComponent] CRITICAL ERROR: 'forks' is still in finalApiParamsToSend despite being deleted!");
            delete finalApiParamsToSend.forks; 
          }
        }
        
        
        
        const restoApiService = new RestoApiService();
        
        finalApiParamsToSend.endpoint = 'autocomplete'; 
        
        const data = await restoApiService.fetchRestos(finalApiParamsToSend);
        
        const results = Array.isArray(data) ? data : [];

        // Sort by city context: when on a city page, prioritize local results
        const cityMatch = window.location.pathname.match(/\/(?:city|stad|ville)\/([^/?#]+)/);
        const cityContext = cityMatch ? decodeURIComponent(cityMatch[1]).replace(/-/g, ' ').toLowerCase() : null;

        let sortedResults = results;
        if (cityContext) {
          sortedResults = [...results].sort((a, b) => {
            const aLocal = a.address?.toLowerCase().includes(cityContext) ? 0 : 1;
            const bLocal = b.address?.toLowerCase().includes(cityContext) ? 0 : 1;
            return aLocal - bLocal;
          });
        }

        setSuggestions(sortedResults);
        setDisplaySuggestions(sortedResults);
        
        // Track search query
        const analytics = AnalyticsManager.getInstance();
        analytics.trackSearch(query, results.length);
      } catch (error) {
        console.error("Error fetching suggestions:", error);
        setSuggestions([]); 
      } finally {
        setIsLoading(false);
      }
    }, 200), 
    [API_BASE_URL, mapBounds, selectedEmojis], 
  );

  useEffect(() => {
    
    
    
    if (inputValue && inputValue.length >= 2 && !isSuggestionSelected) {
      
      fetchSuggestions(inputValue);
    } else {
      // Input too short or suggestion selected - don't refetch
    }
  }, [selectedEmojis, fetchSuggestions, inputValue, isSuggestionSelected]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setIsSuggestionSelected(false); 
    
    // Only clear suggestions when input is empty or too short
    if (!value || value.length < 2) {
      setSuggestions([]);
      setDisplaySuggestions([]);
    }
    
    fetchSuggestions(value);
    setActiveSuggestionIndex(-1); 
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setInputValue(suggestion.name + (suggestion.address ? `, ${suggestion.address}` : ""));
    setSuggestions([]);
    setDisplaySuggestions([]);
    setIsSuggestionSelected(true);
    setIsFocused(false);

    const location = {
      lat: suggestion.latitude,
      lng: suggestion.longitude,
    };

    onRestaurantSelect({ id: suggestion.id, location: location });

    // Track restaurant selection from search
    const analytics = AnalyticsManager.getInstance();
    analytics.trackRestaurantView(Number(suggestion.id), suggestion.name);

    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleRecentlyViewedClick = (suggestion: Suggestion) => {
    AnalyticsManager.getInstance().trackEvent('recent_restaurant_select', {
      restaurant_id: suggestion.id,
      restaurant_name: suggestion.name,
      event_category: 'engagement',
    });
    handleSuggestionClick(suggestion);
  };

  useEffect(() => {
    if (activeSuggestionIndex >= 0 && activeSuggestionRef.current) {
      activeSuggestionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeSuggestionIndex]);

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape" && suggestions.length === 0) {
      setIsFocused(false);
      return;
    }
    if (suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => (prevIndex < suggestions.length - 1 ? prevIndex + 1 : prevIndex));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveSuggestionIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : 0));
        break;
      case "Enter":
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setSuggestions([]);
        setDisplaySuggestions([]);
        setActiveSuggestionIndex(-1);
        setIsFocused(false);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        (!suggestionsRef.current || !suggestionsRef.current.contains(event.target as Node))
      ) {
        setSuggestions([]);
        setDisplaySuggestions([]);
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Listen for hero overlay search focus event
  useEffect(() => {
    const handleHeroSearchFocus = () => {
      inputRef.current?.focus();
    };
    window.addEventListener('heroSearchFocus', handleHeroSearchFocus);
    return () => {
      window.removeEventListener('heroSearchFocus', handleHeroSearchFocus);
    };
  }, []);

  const handleFormSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
  }, []);

  const handleEmojiSelectionChange = (updatedSelectedEmojis: boolean[]) => {
    const newSelectedEmojisState: boolean[] = [...updatedSelectedEmojis]; 
    
    const allDeselected = newSelectedEmojisState.every(selected => !selected);
    
    if (allDeselected) {
      // All emojis deselected
    } else {
      // At least one emoji selected
    }
    
    setSelectedEmojis(newSelectedEmojisState);

    const emojiScores = newSelectedEmojisState
      .map((isSelected: boolean, index: number) => isSelected ? mapIndexToScore(index) : -1)
      .filter((score: number) => score !== -1);

    
    
    if (onEmojiRangeChange) {
      if (emojiScores.length === 0) {
        onEmojiRangeChange([]);
        
      } else {
        onEmojiRangeChange(emojiScores);
        
      }
    }
    
    if (inputValue && inputValue.length >= 2) {
      
      fetchSuggestions(inputValue);
    }
  };

  const handleEmojiClick = (index: number) => {
    // Track emoji filter changes
    const analytics = AnalyticsManager.getInstance();
    analytics.trackEvent('filter_emoji_click', {
      emoji_index: index + 1,
      is_selecting: !selectedEmojis[index],
      event_category: 'filters'
    });
  };

  return (
    <div className="fade-in smooth-transition">
      <div className="m-2 flex flex-col items-center justify-start z-10 rounded-xl bg-gray-50 dark:bg-gray-900 shadow-lg border border-gray-200/50 dark:border-gray-700/50 relative overflow-visible">
        <div className="w-full px-6 pt-6 pb-4 overflow-visible">
          <form className="w-full md:max-w-[500px] mx-auto" onSubmit={handleFormSubmit}>
            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                <SearchIcon />
              </div>
              <input
                ref={inputRef}
                type="search"
                id="autocomplete-search"
                className="block w-full p-4 ps-10 text-sm text-gray-900 dark:text-white border border-gray-300/50 dark:border-gray-600/50 rounded-lg bg-gray-100/70 dark:bg-gray-800/70 focus:ring-blue-400 focus:border-blue-400 placeholder-gray-500 dark:placeholder-gray-400 shadow-inner transition-all duration-200 smooth-transition"
                placeholder={t('search.enterRestaurantName')}
                value={inputValue}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onClick={() => inputRef.current?.select()}
                autoComplete="off"
              />
              {(isLoading || displaySuggestions.length > 0) && inputValue.length >= 2 && (
                <ul
                  ref={suggestionsRef}
                  className={`absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-y-auto dropdown-menu`}
                  style={{ minHeight: displaySuggestions.length > 0 ? `${Math.min(displaySuggestions.length, 10) * 60}px` : '60px' }}
                >
                  {displaySuggestions.map((suggestion, index) => (
                      <SuggestionListItem
                        key={suggestion.id}
                        suggestion={suggestion}
                        isActive={index === activeSuggestionIndex}
                        activeRef={index === activeSuggestionIndex ? activeSuggestionRef : null}
                        onClick={handleSuggestionClick}
                        searchQuery={inputValue}
                      />
                    ))}
                  {displaySuggestions.length === 0 && inputValue.length >= 2 && (
                    <StatusItem message={isLoading ? t('search.loading') : t('search.noResultsFound')} />
                  )}
                </ul>
              )}
              {showRecentlyViewed && (
                <ul
                  ref={suggestionsRef}
                  className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg max-h-80 overflow-y-auto dropdown-menu"
                  data-testid="recently-viewed-list"
                >
                  <li className="flex items-center justify-between px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-300 select-none">
                    {t('search.recentlyViewed')}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => clearRecentlyViewed()}
                      className="font-normal normal-case text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      {t('search.clearRecent')}
                    </button>
                  </li>
                  {recentlyViewed.map((entry) => (
                    <SuggestionListItem
                      key={entry.id}
                      suggestion={entry}
                      isActive={false}
                      onClick={handleRecentlyViewedClick}
                      searchQuery=""
                    />
                  ))}
                </ul>
              )}
            </div>
            <EmojiSelector
              emojis={emojis}
              onEmojiClick={handleEmojiClick}
              selectedEmojis={selectedEmojis}
              onSelectionChange={handleEmojiSelectionChange}
            />
            <div
              className="text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer mt-3"
              onClick={() => openModal('emojiExplanation')}
            >
              {t('search.emojiMeaningsQuestion')}
            </div>

          </form>
        </div>
      </div>
      {modals.emojiExplanation && (
        <React.Suspense fallback={null}>
          <EmojiExplanationModal isOpen={modals.emojiExplanation} onClose={() => closeModal('emojiExplanation')} />
        </React.Suspense>
      )}
    </div>
  );
}
